import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  MenuBook as RecipeIcon,
  Save as SaveIcon,
  DeleteOutline as RemoveIcon,
  Add as AddIcon,
  BlockOutlined as DeactivateIcon,
} from '@mui/icons-material';
import debounce from 'lodash/debounce';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import recipeService, { type RecipeLineInput } from '../../../../services/recipeService';
import inventoryItemService, { type InventoryItemWithBalance } from '../../../../services/inventoryItemService';
import type { Recipe, RecipeLine, InventoryItemType, SizeUnit } from '../../../../types/venue';

// ── Constants ─────────────────────────────────────────────────────────────────

const SIZE_UNITS: SizeUnit[] = ['ml', 'cl', 'l', 'g', 'kg', 'unit'];

const VOLUME_UNITS = new Set<string>(['ml', 'cl', 'l']);
const MASS_UNITS = new Set<string>(['g', 'kg']);

function unitFamily(u: string | null | undefined): 'volume' | 'mass' | null {
  if (!u) return null;
  if (VOLUME_UNITS.has(u)) return 'volume';
  if (MASS_UNITS.has(u)) return 'mass';
  return null;
}

function hasUnitMismatch(recipeUnit: string | '', itemSizeUnit: string | null | undefined): boolean {
  if (!recipeUnit || !itemSizeUnit) return false;
  const rf = unitFamily(recipeUnit);
  const itemF = unitFamily(itemSizeUnit);
  return rf !== null && itemF !== null && rf !== itemF;
}

const VOLUME_TO_ML: Record<string, number> = { ml: 1, cl: 10, l: 1000 };

function volTinyWarn(qty: string, unit: SizeUnit | ''): string | null {
  if (!unit || !qty) return null;
  const factor = VOLUME_TO_ML[unit];
  if (!factor) return null;
  const qtyN = parseFloat(qty);
  if (isNaN(qtyN) || qtyN <= 0) return null;
  const inMl = qtyN * factor;
  if (inMl < 5) {
    return `Very small volume (≈ ${inMl < 1 ? inMl.toPrecision(3) : inMl.toFixed(1)} ml). Did you mean ${qtyN} unit (stock fraction) instead?`;
  }
  return null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// displayName is canonical and already includes size after E3-16 — do not append size again.
const formatItemLabel = (item: InventoryItemWithBalance): string => item.displayName ?? item._id;

// ── Draft line type (before save) ─────────────────────────────────────────────

interface DraftLine {
  key: string;
  item: InventoryItemWithBalance;
  quantity: string;
  sizeUnit: SizeUnit | '';
  notes: string;
}

let lineKeyCounter = 0;
const nextKey = () => `line-${++lineKeyCounter}`;

// ── Item Autocomplete ─────────────────────────────────────────────────────────

interface ItemAutocompleteProps {
  venueId: string;
  label: string;
  typeFilter?: InventoryItemType;
  value: InventoryItemWithBalance | null;
  onChange: (item: InventoryItemWithBalance | null) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  helperText?: string;
}

const ItemAutocomplete: React.FC<ItemAutocompleteProps> = ({
  venueId, label, typeFilter, value, onChange, disabled, size = 'small', helperText,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<InventoryItemWithBalance[]>([]);
  const [searching, setSearching] = useState(false);

  const search = useRef(
    debounce(async (q: string) => {
      if (!venueId) return;
      setSearching(true);
      try {
        const result = await inventoryItemService.getItems(venueId, 1, 50, 'displayName', 'asc', q || undefined);
        const filtered = typeFilter
          ? result.data.filter(i => i.type === typeFilter)
          : result.data.filter(i => i.active);
        setOptions(filtered);
      } catch {
        setOptions([]);
      } finally {
        setSearching(false);
      }
    }, 300),
  ).current;

  useEffect(() => {
    search(inputValue);
    return () => { search.cancel(); };
  }, [inputValue, search]);

  return (
    <Autocomplete
      size={size}
      options={options}
      value={value}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      onChange={(_, v) => onChange(v)}
      getOptionLabel={opt => formatItemLabel(opt)}
      isOptionEqualToValue={(a, b) => a._id === b._id}
      loading={searching}
      disabled={disabled}
      filterOptions={x => x}
      noOptionsText={inputValue.length < 1 ? 'Type to search items…' : 'No matching items'}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {searching && <CircularProgress size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};

// ── RecipeDetail ──────────────────────────────────────────────────────────────

const RecipeDetail: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId } = useVenue();
  const { recipeId } = useParams<{ recipeId: string }>();
  const isCreate = !recipeId;
  const backPath = `/admin/venues/${venueId}/recipes`;

  // ── Recipe state ─────────────────────────────────────────────────────────
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(!isCreate);
  const [loadError, setLoadError] = useState<string | null>(null);

  // ── Header form ──────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [outputItem, setOutputItem] = useState<InventoryItemWithBalance | null>(null);
  const [outputQuantity, setOutputQuantity] = useState('');
  const [yieldPercent, setYieldPercent] = useState('');
  const [notes, setNotes] = useState('');
  const [headerSaving, setHeaderSaving] = useState(false);
  const [headerError, setHeaderError] = useState<string | null>(null);

  // ── Lines state ──────────────────────────────────────────────────────────
  const [existingLines, setExistingLines] = useState<RecipeLine[]>([]);
  const [draftLines, setDraftLines] = useState<DraftLine[]>([]);
  const [linesDirty, setLinesDirty] = useState(false);
  const [linesSaving, setLinesSaving] = useState(false);
  const [linesError, setLinesError] = useState<string | null>(null);

  // ── Add-line form ────────────────────────────────────────────────────────
  const [addItem, setAddItem] = useState<InventoryItemWithBalance | null>(null);
  const [addQty, setAddQty] = useState('');
  const [addSizeUnit, setAddSizeUnit] = useState<SizeUnit | ''>('');
  const [addNotes, setAddNotes] = useState('');

  // ── Deactivate ───────────────────────────────────────────────────────────
  const [deactivating, setDeactivating] = useState(false);

  // ── Load existing recipe ─────────────────────────────────────────────────
  const loadRecipe = useCallback(async () => {
    if (!recipeId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const { recipe: r, lines } = await recipeService.getRecipe(venueId, recipeId);
      setRecipe(r);
      setName(r.name);
      setOutputQuantity(String(r.outputQuantity));
      setYieldPercent(r.yieldPercent != null ? String(r.yieldPercent) : '');
      setNotes(r.notes ?? '');
      setExistingLines(lines);
      setDraftLines(lines.map(l => ({
        key: nextKey(),
        item: { _id: l.inventoryItemId, displayName: l.displayName ?? '' } as InventoryItemWithBalance,
        quantity: String(l.quantity),
        sizeUnit: (l.sizeUnit as SizeUnit | '') ?? '',
        notes: l.notes ?? '',
      })));
      setLinesDirty(false);
    } catch (err: any) {
      setLoadError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to load recipe');
    } finally {
      setLoading(false);
    }
  }, [venueId, recipeId]);

  useEffect(() => { if (!isCreate) loadRecipe(); }, [isCreate, loadRecipe]);

  // ── Resolve display names for draft lines loaded from server ─────────────
  useEffect(() => {
    if (draftLines.length === 0 || !existingLines.length) return;
    const needsResolution = draftLines.some(d => !d.item.displayName);
    if (!needsResolution) return;

    draftLines.forEach(async (d, i) => {
      if (d.item.displayName) return;
      try {
        const fullItem = await inventoryItemService.getItem(venueId, d.item._id);
        setDraftLines(prev => prev.map((l, idx) =>
          idx === i ? { ...l, item: { ...l.item, ...fullItem, totalQuantity: 0, balance: null } as InventoryItemWithBalance } : l,
        ));
      } catch { /* keep stub */ }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingLines.length]);

  // ── Header save (create or update) ───────────────────────────────────────
  const handleHeaderSave = async () => {
    setHeaderError(null);

    if (!name.trim()) { setHeaderError('Recipe name is required.'); return; }
    const qty = parseFloat(outputQuantity);
    if (!outputQuantity || isNaN(qty) || qty < 0.001) {
      setHeaderError('Output quantity must be a number greater than 0.');
      return;
    }
    if (yieldPercent) {
      const yp = parseFloat(yieldPercent);
      if (isNaN(yp) || yp < 1 || yp > 100) {
        setHeaderError('Yield % must be between 1 and 100.');
        return;
      }
    }

    setHeaderSaving(true);
    try {
      if (isCreate) {
        if (!outputItem) { setHeaderError('Output inventory item is required.'); setHeaderSaving(false); return; }
        const created = await recipeService.createRecipe(venueId, {
          name: name.trim(),
          outputInventoryItemId: outputItem._id,
          outputQuantity: qty,
          yieldPercent: yieldPercent ? parseFloat(yieldPercent) : undefined,
          notes: notes.trim() || undefined,
        });
        navigate(`${backPath}/${created._id}`);
      } else {
        const { recipe: updated } = await recipeService.updateRecipe(venueId, recipeId!, {
          name: name.trim(),
          outputQuantity: qty,
          yieldPercent: yieldPercent ? parseFloat(yieldPercent) : null,
          notes: notes.trim() || null,
        });
        setRecipe(updated);
      }
    } catch (err: any) {
      setHeaderError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to save recipe');
    } finally {
      setHeaderSaving(false);
    }
  };

  // ── Add line to draft ─────────────────────────────────────────────────────
  const handleAddLine = () => {
    if (!addItem) return;
    const qty = parseFloat(addQty);
    if (!addQty || isNaN(qty) || qty < 0.001) return;

    const alreadyAdded = draftLines.some(d => d.item._id === addItem._id);
    if (alreadyAdded) {
      setLinesError(`"${addItem.displayName}" is already in the ingredient list.`);
      return;
    }

    setDraftLines(prev => [...prev, {
      key: nextKey(),
      item: addItem,
      quantity: addQty,
      sizeUnit: addSizeUnit,
      notes: addNotes.trim(),
    }]);
    setLinesDirty(true);
    setAddItem(null);
    setAddQty('');
    setAddSizeUnit('');
    setAddNotes('');
    setLinesError(null);
  };

  const handleRemoveLine = (key: string) => {
    setDraftLines(prev => prev.filter(d => d.key !== key));
    setLinesDirty(true);
  };

  // ── Save lines (replace-all) ──────────────────────────────────────────────
  const handleSaveLines = async () => {
    if (!recipeId) return;
    setLinesError(null);

    if (draftLines.length === 0) {
      setLinesError('A recipe must have at least one ingredient line.');
      return;
    }

    for (let i = 0; i < draftLines.length; i++) {
      const d = draftLines[i];
      const qty = parseFloat(d.quantity);
      if (!d.quantity || isNaN(qty) || qty < 0.001) {
        setLinesError(`Line ${i + 1}: quantity must be a number greater than 0.`);
        return;
      }
    }

    setLinesSaving(true);
    try {
      const payload: RecipeLineInput[] = draftLines.map((d, idx) => ({
        inventoryItemId: d.item._id,
        quantity: parseFloat(d.quantity),
        sizeUnit: d.sizeUnit || undefined,
        position: idx,
        notes: d.notes || undefined,
      }));
      const { recipe: updated, lines } = await recipeService.replaceRecipeLines(venueId, recipeId!, payload);
      setRecipe(updated);
      setExistingLines(lines);
      setLinesDirty(false);
    } catch (err: any) {
      setLinesError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to save ingredient lines');
    } finally {
      setLinesSaving(false);
    }
  };

  // ── Deactivate ───────────────────────────────────────────────────────────
  const handleDeactivate = async () => {
    if (!recipeId) return;
    if (!window.confirm(`Deactivate recipe "${recipe?.name}"? It will no longer be available for new production orders.`)) return;
    setDeactivating(true);
    try {
      const updated = await recipeService.deactivateRecipe(venueId, recipeId);
      setRecipe(updated);
    } catch (err: any) {
      setHeaderError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to deactivate recipe');
    } finally {
      setDeactivating(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">Loading recipe…</Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  }

  const isInactive = recipe && !recipe.active;
  const canEdit = !isInactive;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* ── Recipe header card ── */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', maxWidth: 720 }}>
        {/* Header bar */}
        <Box sx={{
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'center', gap: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(backPath)}
            sx={{ textTransform: 'none', color: theme.palette.text.secondary, minWidth: 'auto' }}
          >
            Recipes
          </Button>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <RecipeIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
            <Typography variant="h6" fontWeight={600}>
              {isCreate ? 'New Production Recipe' : (recipe?.name ?? 'Production Recipe')}
            </Typography>
          </Box>
          {recipe && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={`v${recipe.version}`}
                size="small"
                sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }}
              />
              <Chip
                label={recipe.active ? 'Active' : 'Inactive'}
                size="small"
                color={recipe.active ? 'success' : 'default'}
                sx={{ fontWeight: 500 }}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {isInactive && (
            <Alert severity="warning">This recipe is inactive and cannot be edited or used in new production orders.</Alert>
          )}
          {headerError && <Alert severity="error" onClose={() => setHeaderError(null)}>{headerError}</Alert>}

          {/* Name */}
          <TextField
            label="Recipe Name"
            value={name}
            onChange={e => setName(e.target.value)}
            size="small"
            fullWidth
            required
            disabled={!canEdit}
            placeholder="e.g. House Cocktail Base, Spiced Syrup"
          />

          {/* Output item — only for create */}
          {isCreate && (
            <ItemAutocomplete
              venueId={venueId}
              label="Output Inventory Item (MANUFACTURED)"
              typeFilter="MANUFACTURED"
              value={outputItem}
              onChange={setOutputItem}
              helperText="Only MANUFACTURED-type inventory items can be recipe outputs."
            />
          )}
          {!isCreate && recipe && (
            <TextField
              label="Output Inventory Item"
              value={recipe.outputInventoryItemId}
              size="small"
              fullWidth
              disabled
              helperText="Output item cannot be changed after recipe creation."
            />
          )}

          {/* Output quantity + yield */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Output Quantity per Batch"
              value={outputQuantity}
              onChange={e => setOutputQuantity(e.target.value)}
              size="small"
              type="number"
              inputProps={{ min: 0.001, step: 'any' }}
              sx={{ flex: 1 }}
              required
              disabled={!canEdit}
              helperText="Units produced per one production run"
            />
            <TextField
              label="Yield % (optional)"
              value={yieldPercent}
              onChange={e => setYieldPercent(e.target.value)}
              size="small"
              type="number"
              inputProps={{ min: 1, max: 100, step: 'any' }}
              sx={{ flex: 1 }}
              disabled={!canEdit}
              helperText="1–100. Reserved for future use."
            />
          </Box>

          {/* Notes */}
          <TextField
            label="Notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
            disabled={!canEdit}
            placeholder="Optional"
          />

          {/* Actions */}
          <Box sx={{
            pt: 1.5,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            display: 'flex', gap: 1.5, justifyContent: 'flex-end', alignItems: 'center',
          }}>
            {recipe?.active && (
              <Button
                color="inherit"
                startIcon={deactivating ? <CircularProgress size={16} /> : <DeactivateIcon />}
                onClick={handleDeactivate}
                disabled={deactivating}
                sx={{ textTransform: 'none', color: theme.palette.text.secondary, mr: 'auto' }}
              >
                Deactivate
              </Button>
            )}
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={headerSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleHeaderSave}
              disabled={headerSaving || !canEdit}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {headerSaving ? 'Saving…' : (isCreate ? 'Create Recipe' : 'Save Changes')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* ── Recipe lines editor (edit mode only) ── */}
      {!isCreate && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box sx={{
            px: 3, py: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}>
            <Typography variant="subtitle1" fontWeight={600}>Ingredients</Typography>
            {linesDirty && (
              <Chip label="Unsaved changes" size="small" color="warning" sx={{ fontWeight: 500 }} />
            )}
          </Box>

          {linesError && (
            <Box sx={{ px: 3, pt: 2 }}>
              <Alert severity="error" onClose={() => setLinesError(null)}>{linesError}</Alert>
            </Box>
          )}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['#', 'Ingredient', 'Qty per batch', 'Unit', 'Notes', ''].map(h => (
                    <TableCell
                      key={h}
                      sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase' }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {draftLines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                        No ingredients yet. Add at least one ingredient below.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {draftLines.map((d, idx) => (
                  <TableRow key={d.key}>
                    <TableCell sx={{ color: theme.palette.text.disabled, width: 32 }}>
                      <Typography variant="caption">{idx + 1}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {d.item.displayName ?? d.item._id}
                      </Typography>
                      {d.item.type && (
                        <Typography variant="caption" color="text.secondary">{d.item.type}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 110 }}>
                      <TextField
                        size="small"
                        value={d.quantity}
                        onChange={e => {
                          setDraftLines(prev => prev.map(l => l.key === d.key ? { ...l, quantity: e.target.value } : l));
                          setLinesDirty(true);
                        }}
                        type="number"
                        inputProps={{ min: 0.001, step: 'any' }}
                        disabled={!canEdit}
                        sx={{ width: 90 }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 100 }}>
                      <TextField
                        select
                        size="small"
                        value={d.sizeUnit}
                        onChange={e => {
                          setDraftLines(prev => prev.map(l => l.key === d.key ? { ...l, sizeUnit: e.target.value as SizeUnit | '' } : l));
                          setLinesDirty(true);
                        }}
                        disabled={!canEdit}
                        sx={{ width: 90 }}
                      >
                        <MenuItem value=""><em>—</em></MenuItem>
                        {SIZE_UNITS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                      </TextField>
                      {hasUnitMismatch(d.sizeUnit, d.item.sizeUnit) && (
                        <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.25, fontSize: '0.65rem' }}>
                          ⚠ item unit: {d.item.sizeUnit}
                        </Typography>
                      )}
                      {volTinyWarn(d.quantity, d.sizeUnit) && (
                        <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.25, fontSize: '0.65rem' }}>
                          ⚠ {volTinyWarn(d.quantity, d.sizeUnit)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={d.notes}
                        onChange={e => {
                          setDraftLines(prev => prev.map(l => l.key === d.key ? { ...l, notes: e.target.value } : l));
                          setLinesDirty(true);
                        }}
                        placeholder="—"
                        disabled={!canEdit}
                        sx={{ width: 160 }}
                        inputProps={{ maxLength: 500 }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ width: 48 }}>
                      {canEdit && (
                        <Tooltip title="Remove ingredient">
                          <IconButton size="small" onClick={() => handleRemoveLine(d.key)} sx={{ color: theme.palette.text.disabled }}>
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Add ingredient row */}
                {canEdit && (
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                    <TableCell />
                    <TableCell>
                      <ItemAutocomplete
                        venueId={venueId}
                        label="Add ingredient…"
                        value={addItem}
                        onChange={item => {
                          setAddItem(item);
                          setAddSizeUnit(item?.sizeUnit ? (item.sizeUnit as SizeUnit) : '');
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={addQty}
                        onChange={e => setAddQty(e.target.value)}
                        type="number"
                        inputProps={{ min: 0.001, step: 'any' }}
                        placeholder="Qty"
                        sx={{ width: 90 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={addSizeUnit}
                        onChange={e => setAddSizeUnit(e.target.value as SizeUnit | '')}
                        sx={{ width: 90 }}
                        helperText="ml/g = measured; unit = stock fraction"
                      >
                        <MenuItem value=""><em>—</em></MenuItem>
                        {SIZE_UNITS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                      </TextField>
                      {hasUnitMismatch(addSizeUnit, addItem?.sizeUnit) && (
                        <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.25, fontSize: '0.65rem' }}>
                          ⚠ unit family mismatch with item ({addItem?.sizeUnit})
                        </Typography>
                      )}
                      {volTinyWarn(addQty, addSizeUnit) && (
                        <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.25, fontSize: '0.65rem' }}>
                          ⚠ {volTinyWarn(addQty, addSizeUnit)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={addNotes}
                        onChange={e => setAddNotes(e.target.value)}
                        placeholder="Notes"
                        sx={{ width: 160 }}
                        inputProps={{ maxLength: 500 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Add ingredient">
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleAddLine}
                            disabled={!addItem || !addQty || parseFloat(addQty) < 0.001}
                            color="primary"
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {canEdit && (
            <Box sx={{
              px: 3, py: 2,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              display: 'flex', justifyContent: 'flex-end', gap: 1.5,
            }}>
              <Button
                variant="contained"
                startIcon={linesSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveLines}
                disabled={linesSaving || !linesDirty}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {linesSaving ? 'Saving…' : 'Save Ingredients'}
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default RecipeDetail;
